import { useEffect, useId, useRef, useState } from 'react';
import type { BucketProfile } from '@oysterjs/smartfill-contracts';
import { ChevronRightIcon } from './Icons';

export interface PickerOption {
  value: string;
  label: string;
  description: string;
}

export function profileOptions(profiles: BucketProfile[]): PickerOption[] {
  return profiles.map((profile) => ({
    value: profile.id,
    label: profile.name,
    description:
      profile.carriers.length === 1 ? profile.carriers[0] : `${profile.carriers.length} carriers`
  }));
}

/**
 * A search box whose matches open below it, in the panel's own flow rather
 * than over it. The choice changes only when an option is picked; leaving the
 * box puts the current choice back.
 */
export function ProfilePicker({
  label,
  options,
  value,
  placeholder,
  onChange
}: {
  label: string;
  options: PickerOption[];
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const selected = options.find((option) => option.value === value) ?? null;
  const needle = query.trim().toLowerCase();
  const matches = needle
    ? options.filter((option) =>
        `${option.label} ${option.description}`.toLowerCase().includes(needle)
      )
    : options;
  const optionId = (index: number) => `${id}-option-${index}`;

  useEffect(() => {
    if (open)
      document.getElementById(`${id}-option-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, id, open]);

  const show = () => {
    if (open) return;
    setQuery('');
    setActive(
      Math.max(
        0,
        options.findIndex((option) => option.value === value)
      )
    );
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  const pick = (option: PickerOption) => {
    close();
    if (option.value !== value) onChange(option.value);
  };

  return (
    <div className="profile-picker">
      <label className="profile-picker__label" htmlFor={`${id}-input`}>
        {label}
      </label>
      <div className="profile-picker__box">
        <input
          ref={input}
          id={`${id}-input`}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-options`}
          aria-autocomplete="list"
          aria-activedescendant={open && matches[active] ? optionId(active) : undefined}
          autoComplete="off"
          placeholder={open && selected ? selected.label : placeholder}
          value={open ? query : (selected?.label ?? '')}
          onFocus={show}
          onClick={show}
          onBlur={close}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              if (!open) show();
              else setActive((index) => Math.min(index + 1, matches.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter' && open) {
              event.preventDefault();
              if (matches[active]) pick(matches[active]);
            } else if (event.key === 'Escape' && open) {
              event.preventDefault();
              close();
            }
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          className={`profile-picker__toggle${open ? ' is-open' : ''}`}
          aria-label={open ? `Close ${label}` : `Open ${label}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (open) {
              close();
            } else {
              input.current?.focus();
              show();
            }
          }}
        >
          <ChevronRightIcon />
        </button>
      </div>
      {open && (
        <ul
          className="profile-picker__options"
          id={`${id}-options`}
          role="listbox"
          aria-label={label}
        >
          {matches.length === 0 && <li className="profile-picker__empty">No matches</li>}
          {matches.map((option, index) => (
            <li
              key={option.value}
              id={optionId(index)}
              role="option"
              aria-selected={option.value === value}
              className={`profile-picker__option${index === active ? ' is-active' : ''}${
                option.value === value ? ' is-selected' : ''
              }`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => pick(option)}
            >
              <span className="profile-picker__name">{option.label}</span>
              <span className="profile-picker__detail">{option.description}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
